// src/pages/StarRec/Blacklists/JobHolders.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as blacklistApi from "../../../api/blacklist/blacklistApi";

/* -------------------- SIMPLE MESSAGE BAR -------------------- */
function MessageBar({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="alert alert-info d-flex justify-content-between align-items-center">
      <div>{message}</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

/* -------------------- SIMPLE MODAL -------------------- */
function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border rounded p-3"
        style={{ width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* -------------------- VIEW BLACKLISTED JOB HOLDER INFO -------------------- */
function ViewBlacklistedJobHolderModal({ open, item, onClose, onUnBlacklist, busy }) {
  if (!open || !item) return null;

  const Field = ({ label, value }) => (
    <div className="mb-3">
      <label className="form-label fw-semibold">{label}</label>
      <input className="form-control" value={value ?? ""} readOnly />
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="View Blacklisted Job Holder Info">
      <div className="row">
        <div className="col-md-6">
          <Field label="Full Name" value={item.fullName} />
        </div>
        <div className="col-md-6">
          <Field label="Position" value={item.position} />
        </div>

        <div className="col-md-6">
          <Field label="TAX ID" value={item.taxId} />
        </div>
        <div className="col-md-6">
          <Field label="Business Name" value={item.businessName} />
        </div>

        <div className="col-md-6">
          <Field label="Date of Joining" value={item.dateOfJoining} />
        </div>
        <div className="col-md-6">
          <Field label="CTC" value={item.ctc} />
        </div>

        <div className="col-md-6">
          <Field label="Updated CTC - Date" value={item.updatedCtcDate} />
        </div>
        <div className="col-md-6">
          <Field label="Updated CTC - CTC" value={item.updatedCtcCtc} />
        </div>

        <div className="col-md-6">
          <Field label="Date of Exit" value={item.dateOfExit} />
        </div>
        <div className="col-md-6">
          <Field label="Didn't Exit" value={item.didntExit ? "Yes" : "No"} />
        </div>

        <div className="col-md-6">
          <Field label="Personal Email ID" value={item.personalEmail} />
        </div>
        <div className="col-md-6">
          <Field label="Mobile Number" value={item.mobileNumber} />
        </div>

        <div className="col-12">
          <Field label="Blacklist Reason" value={item.blacklistReason} />
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
          Back
        </button>
        <button
          className="btn btn-outline-success"
          onClick={() => onUnBlacklist(item.id)}
          disabled={busy}
        >
          {busy ? "..." : "Un-Blacklist"}
        </button>
      </div>
    </Modal>
  );
}

/* -------------------- HELPERS -------------------- */
function safeText(v) {
  return (v ?? "").toString().trim();
}

const JobHolders = () => {
  const didLoadRef = useRef(false);

  useEffect(() => {
    document.title = "StarRec Blacklist JobHolders";
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  /* -------------------- FILTERS -------------------- */
  const [filterCountry, setFilterCountry] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");

  /* -------------------- VIEW -------------------- */
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [message, setMessage] = useState("");
  const showMessage = (txt) => {
    setMessage(txt);
    window.clearTimeout(window.__msgTimer);
    window.__msgTimer = window.setTimeout(() => setMessage(""), 2000);
  };

  const normalizeEmployee = (e) => ({
    id: (e?._id || e?.id || "").toString(),
    fullName: e?.fullName || [e?.firstName, e?.lastName].filter(Boolean).join(" "),
    starRecId: e?.starRecId || e?.employeeId || "",
    taxId: e?.taxId || "",
    businessName: e?.businessName || "",
    country: e?.country || "",
    state: e?.state || "",
    city: e?.city || "",
    position: e?.position || e?.role || "",
    dateOfJoining: e?.dateOfJoining || "",
    ctc: e?.ctc || "",
    updatedCtcDate: e?.updatedCtcDate || "",
    updatedCtcCtc: e?.updatedCtcCtc || "",
    dateOfExit: e?.dateOfExit || "",
    didntExit: Boolean(e?.didntExit),
    personalEmail: e?.personalEmail || e?.email || "",
    mobileNumber: e?.mobileNumber || e?.mobile || "",
    blacklistReason: e?.blacklistReason || e?.reason || "",
    raw: e,
  });

  const loadBlacklistedEmployees = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await blacklistApi.getBlacklisted();
      const employees = Array.isArray(res?.employees) ? res.employees : [];
      setRows(employees.map(normalizeEmployee));
    } catch (e) {
      setError(e?.message || "Failed to load blacklisted job holders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    loadBlacklistedEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countries = useMemo(
    () => Array.from(new Set(rows.map((r) => r.country).filter(Boolean))).sort(),
    [rows]
  );

  const states = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .map((r) => r.state)
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry]);

  const cities = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .filter((r) => (filterState ? r.state === filterState : true))
      .map((r) => r.city)
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry, filterState]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterCountry && r.country !== filterCountry) return false;
      if (filterState && r.state !== filterState) return false;
      if (filterCity && r.city !== filterCity) return false;
      return true;
    });
  }, [rows, filterCountry, filterState, filterCity]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId]
  );

  const openView = (row) => {
    setSelectedId(row.id);
    setViewOpen(true);
  };

  const closeView = () => {
    setViewOpen(false);
    setSelectedId(null);
  };

  const unBlacklist = async (id) => {
    setBusy(true);
    try {
      await blacklistApi.unblacklistEmployee(id);
      showMessage("Un-blacklisted successfully");
      closeView();
      await loadBlacklistedEmployees();
    } catch (e) {
      alert(e?.message || "Un-blacklist failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="fs-4 fw-bold m-0">Manage Job Holders (Blacklisted)</h1>
        <button className="btn btn-outline-primary" onClick={loadBlacklistedEmployees} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <MessageBar message={message} onClose={() => setMessage("")} />

      {error ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <div>{error}</div>
          <button className="btn btn-sm btn-outline-danger" onClick={loadBlacklistedEmployees}>
            Retry
          </button>
        </div>
      ) : null}

      {/* FILTERS */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <label className="form-label">Country</label>
          <select
            className="form-select"
            value={filterCountry}
            onChange={(e) => {
              setFilterCountry(e.target.value);
              setFilterState("");
              setFilterCity("");
            }}
          >
            <option value="">All</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            className="form-select"
            value={filterState}
            onChange={(e) => {
              setFilterState(e.target.value);
              setFilterCity("");
            }}
            disabled={!filterCountry}
          >
            <option value="">All</option>
            {states.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">City</label>
          <select
            className="form-select"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            disabled={!filterState}
          >
            <option value="">All</option>
            {cities.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* TABLE LIST */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead>
            <tr>
              <th>Job Holder Name</th>
              <th>StarRec ID</th>
              <th>TAX ID</th>
              <th>Current Business Name</th>
              <th style={{ width: 120 }} className="text-center">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center">
                  No blacklisted job holders found.
                </td>
              </tr>
            ) : (
              filteredRows.map((r) => (
                <tr key={r.id} className="table-danger">
                  <td>{r.fullName || "-"}</td>
                  <td>{r.starRecId || "-"}</td>
                  <td>{r.taxId || "-"}</td>
                  <td>{r.businessName || "-"}</td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(r)}>
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ViewBlacklistedJobHolderModal
        open={viewOpen}
        item={selected}
        onClose={closeView}
        onUnBlacklist={unBlacklist}
        busy={busy}
      />
    </div>
  );
};

export default JobHolders;
